import React, { useEffect, useRef } from 'react';
import WizardSelectControlledComponent from 'components/v2/epd-wizard-inputs/WizardSelectControlled';
import { PcrSelectorRecord } from 'types/dynamic-forms/records/PcrSelectorRecord';
import { useDocumentStore } from '../hooks/useDocumentStore';
import usePcrSelection from '../hooks/usePcrSelection';
import { useDigitalVersionSwitch } from '../hooks/useDigitalVersionSwitch';

const PcrSelectorRenderer: React.FC<PcrSelectorRecord> = ({ label, tooltip }) => {
  const store = useDocumentStore();
  const { switchDigitalVersion } = useDigitalVersionSwitch();
  const { selection, pcrOptions, productCategoryId, pcrErrorMessage, isDisabled } = usePcrSelection();

  // A product category change can take the selected PCR out of the options. The PCR is then cleared, as the form
  // did when the PCR was document data; only a change counts, not the value the form was opened with.
  // The category change and the PCR reset are one edit: when the reset is cancelled or fails, the category goes back.
  const previousProductCategoryId = useRef(productCategoryId);
  useEffect(() => {
    if (previousProductCategoryId.current === productCategoryId) {
      return;
    }
    const restoredProductCategoryId = previousProductCategoryId.current;
    previousProductCategoryId.current = productCategoryId;
    if (isDisabled || !selection.pcrId || pcrOptions.some((o) => o.value === selection.pcrId)) {
      return;
    }
    switchDigitalVersion({
      selection: { pcrId: null, cpcrId: null },
      confirmation: {
        caption: 'Confirm PCR change',
        description: (
          <>
            <p>The selected PCR does not belong to the selected product category and will be removed.</p>
            <p>The fields on the form will be changed which can lead to losing already added data.</p>
            <p>Please press "Confirm" to proceed or press "Cancel" to keep the PCR and the product category.</p>
          </>
        ),
      },
      onCancel: () => {
        // Restoring is not a user change, so it must not check the PCR again.
        previousProductCategoryId.current = restoredProductCategoryId;
        store.setValue(['productCategoryId'], restoredProductCategoryId);
      },
    });
  }, [productCategoryId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = async (nextValue: string | null | undefined) => {
    // Re-selecting the current value must not resolve the digital version again.
    if (!nextValue || nextValue === selection.pcrId) {
      return;
    }
    const selectedOption = pcrOptions.find((o) => o.value === nextValue);
    // The select only offers values from its options; an unknown value is ignored rather than
    // confirmed under its id.
    if (!selectedOption || selectedOption.isDisabled) {
      return;
    }

    // The c-PCR is kept only when the new PCR can be combined with it.
    const keepsCpcr =
      !!selection.cpcrId &&
      store.getCpcrOptions(nextValue).some((o) => o.value === selection.cpcrId && !o.isDisabled);

    await switchDigitalVersion({
      selection: { pcrId: nextValue, cpcrId: keepsCpcr ? selection.cpcrId : null },
      confirmation: {
        caption: 'Confirm PCR change',
        description: (
          <>
            <p>The selected PRC "{selectedOption.label}" is configured to have custom data.</p>
            <p>The fields on the form will be changed which can lead to losing allready added data.</p>
            <p>Please press "Confirm" to procced or press "Cancel" to rollback the PCR selection.</p>
          </>
        ),
      },
    });
  };

  return (
    <WizardSelectControlledComponent
      name="pcrSelector"
      options={pcrOptions}
      value={pcrOptions.find((o) => o.value === selection.pcrId) ?? null}
      label={label}
      tooltip={tooltip}
      onChanged={(_: string, newValue: any) => handleChange(newValue)}
      error={pcrErrorMessage}
      disabled={isDisabled}
    />
  );
};

export default PcrSelectorRenderer;
